import React, {useEffect, useState, useMemo, useCallback} from 'react';
import {useParams, Link as RouterLink} from 'react-router-dom';
import {useMaterialReactTable, MaterialReactTable} from 'material-react-table';
import {
    Box, Typography, Paper, Grid, Stack, Chip, Divider,
    CircularProgress, Alert, Button, Link, LinearProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import apiService from '../../services/apiService';
import {ROLES} from '../../config/roles';

const StatCard = ({label, value}) => (
    <Box sx={{textAlign: 'center'}}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5">{value}</Typography>
    </Box>
);

const StatItem = ({label, value}) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{fontWeight: 'bold'}}>{label}:</Typography>
        <Typography variant="body2">{value}</Typography>
    </Stack>
);

const DistributionBar = ({label, count, maxCount}) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 0.5}}>
        <Typography variant="body2" sx={{minWidth: 50, textAlign: 'right', fontFamily: 'monospace'}}>{label}</Typography>
        <LinearProgress
            variant="determinate"
            value={maxCount > 0 ? (count / maxCount) * 100 : 0}
            sx={{flex: 1, height: 20, borderRadius: 1}}
        />
        <Typography variant="body2" sx={{minWidth: 60, fontFamily: 'monospace'}}>{count.toLocaleString()}</Typography>
    </Stack>
);

const LeaderboardTable = ({title, data, valueColumn, valueLabel}) => {
    const columns = useMemo(() => [
        {
            accessorKey: 'playerUid',
            header: 'Player',
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/players/${cell.getValue()}`}>
                    {cell.getValue()?.substring(0, 16)}...
                </Link>
            ),
        },
        {accessorKey: 'totalPlays', header: 'Plays', size: 80},
        {accessorKey: valueColumn, header: valueLabel, size: 80},
        {accessorKey: 'totalSpent', header: 'Spent', size: 80},
    ], [valueColumn, valueLabel]);

    const table = useMaterialReactTable({
        columns,
        data,
        enablePagination: false,
        enableBottomToolbar: false,
        enableTopToolbar: false,
        enableSorting: false,
        muiTableContainerProps: {sx: {maxHeight: 400}},
    });

    return (
        <Paper elevation={2} sx={{p: 2}}>
            <Typography variant="h6" gutterBottom>{title}</Typography>
            <Divider sx={{mb: 1}}/>
            {data.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{py: 2}}>No data yet.</Typography>
            ) : (
                <MaterialReactTable table={table}/>
            )}
        </Paper>
    );
};

const SessionsTable = ({minigameId}) => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 20});

    const fetchData = useCallback(() => {
        setIsLoading(true);
        const params = new URLSearchParams({
            page: pagination.pageIndex,
            size: pagination.pageSize,
        });
        apiService.get(`/api/minigames/stats/${minigameId}/hilo/sessions?${params}`)
            .then(page => {
                setData(page.content);
                setRowCount(page.totalElements);
                setIsError(false);
            })
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    }, [minigameId, pagination]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const columns = useMemo(() => [
        {
            accessorKey: 'createdAt',
            header: 'Time',
            size: 170,
            Cell: ({cell}) => cell.getValue() ? new Date(cell.getValue()).toLocaleString() : '-',
        },
        {
            accessorKey: 'playerUid',
            header: 'Player',
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/players/${cell.getValue()}`}>
                    {cell.getValue()?.substring(0, 16)}...
                </Link>
            ),
        },
        {accessorKey: 'turnCount', header: 'Turns', size: 70},
        {accessorKey: 'finalStreak', header: 'Streak', size: 70},
        {
            accessorKey: 'continueCount',
            header: 'Continues',
            size: 90,
            Cell: ({cell}) => cell.getValue() > 0
                ? <Chip label={cell.getValue()} color="warning" size="small"/>
                : '0',
        },
        {
            accessorKey: 'accumulatedReward',
            header: 'Reward',
            size: 90,
            Cell: ({cell}) => cell.getValue().toLocaleString(),
        },
        {accessorKey: 'geltsUsed', header: 'Gelts', size: 70},
        {
            accessorKey: 'isWin',
            header: 'Result',
            size: 80,
            Cell: ({cell}) => (
                <Chip
                    label={cell.getValue() ? 'Win' : 'Loss'}
                    color={cell.getValue() ? 'success' : 'error'}
                    size="small"
                />
            ),
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data,
        manualPagination: true,
        rowCount,
        state: {pagination, isLoading, showAlertBanner: isError},
        onPaginationChange: setPagination,
        muiToolbarAlertBannerProps: isError ? {color: 'error', children: 'Error loading sessions'} : undefined,
        enableSorting: false,
    });

    return <MaterialReactTable table={table}/>;
};

const HiLoStatsPage = () => {
    const {minigameId} = useParams();
    const [hiloStats, setHiloStats] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            apiService.get(`/api/minigames/stats/${minigameId}/hilo/overview`),
            apiService.get(`/api/minigames/stats/${minigameId}/leaderboard?sort=wins`),
        ])
            .then(([hilo, lb]) => {
                setHiloStats(hilo);
                setLeaderboard(lb);
            })
            .catch(err => setError(err.message || 'Failed to load HiLo statistics'))
            .finally(() => setLoading(false));
    }, [minigameId]);

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;
    if (error) return <Alert severity="error" sx={{m: 2}}>{error}</Alert>;
    if (!hiloStats) return <Alert severity="info" sx={{m: 2}}>No HiLo data found.</Alert>;

    const s = hiloStats;
    const streakEntries = Object.entries(s.streakBuckets || {});
    const streakMax = Math.max(...streakEntries.map(([, v]) => v), 1);
    const multEntries = Object.entries(s.multiplierDistribution || {});
    const multMax = Math.max(...multEntries.map(([, v]) => v), 1);

    return (
        <Box sx={{p: 2}}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 3}}>
                <Typography variant="h4">HiLo Analytics</Typography>
                <Box sx={{flex: 1}}/>
                <Button component={RouterLink} to={`/minigames/stats/${minigameId}`} variant="outlined"
                        startIcon={<ArrowBackIcon/>}>
                    Back to Statistics
                </Button>
            </Stack>

            {/* KPI Cards */}
            <Paper elevation={2} sx={{p: 2, mb: 3}}>
                <Grid container spacing={3}>
                    <Grid size={{xs: 6, sm: 4, md: 2}}>
                        <StatCard label="Total Sessions" value={s.totalSessions.toLocaleString()}/>
                    </Grid>
                    <Grid size={{xs: 6, sm: 4, md: 2}}>
                        <StatCard label="Unique Players" value={s.uniquePlayers.toLocaleString()}/>
                    </Grid>
                    <Grid size={{xs: 6, sm: 4, md: 2}}>
                        <StatCard label="Avg Streak" value={s.avgStreakPerSession.toFixed(1)}/>
                    </Grid>
                    <Grid size={{xs: 6, sm: 4, md: 2}}>
                        <StatCard label="Max Streak Ever" value={s.maxStreakEver}/>
                    </Grid>
                    <Grid size={{xs: 6, sm: 4, md: 2}}>
                        <StatCard label="Continue Rate" value={`${s.continueConversionRate.toFixed(1)}%`}/>
                    </Grid>
                    <Grid size={{xs: 6, sm: 4, md: 2}}>
                        <StatCard label="Coins/Gelt ROI" value={s.avgCoinsPerGelt.toFixed(1)}/>
                    </Grid>
                </Grid>
            </Paper>

            {/* Monetization */}
            <Grid container spacing={3} sx={{mb: 3}}>
                <Grid size={{xs: 12, md: 6}}>
                    <Paper elevation={2} sx={{p: 2}}>
                        <Typography variant="h6" gutterBottom>Continue Funnel</Typography>
                        <Divider sx={{mb: 2}}/>
                        <Stack spacing={1}>
                            <StatItem label="Total Continues" value={s.totalContinues.toLocaleString()}/>
                            <StatItem label="Conversion Rate" value={`${s.continueConversionRate.toFixed(1)}%`}/>
                            <StatItem label="Avg Continues/Session" value={s.avgContinuesPerSession.toFixed(2)}/>
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min(s.continueConversionRate, 100)}
                            sx={{mt: 2, height: 12, borderRadius: 1}}
                        />
                    </Paper>
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                    <Paper elevation={2} sx={{p: 2}}>
                        <Typography variant="h6" gutterBottom>Gelt Economy</Typography>
                        <Divider sx={{mb: 2}}/>
                        <Stack spacing={1}>
                            <StatItem label="Total Gelts Spent" value={s.totalGeltsSpent.toLocaleString()}/>
                            <StatItem label="Total Coins Awarded" value={s.totalCoinsAwarded.toLocaleString()}/>
                            <StatItem label="Avg Coins/Session" value={s.avgCoinsPerSession.toFixed(0)}/>
                            <StatItem label="ROI (Coins/Gelt)" value={s.avgCoinsPerGelt.toFixed(1)}/>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            {/* Distributions */}
            <Grid container spacing={3} sx={{mb: 3}}>
                <Grid size={{xs: 12, md: 6}}>
                    <Paper elevation={2} sx={{p: 2}}>
                        <Typography variant="h6" gutterBottom>Streak Distribution</Typography>
                        <Divider sx={{mb: 2}}/>
                        {streakEntries.map(([label, count]) => (
                            <DistributionBar key={label} label={label} count={count} maxCount={streakMax}/>
                        ))}
                        {streakEntries.length === 0 && (
                            <Typography variant="body2" color="text.secondary">No data.</Typography>
                        )}
                    </Paper>
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                    <Paper elevation={2} sx={{p: 2}}>
                        <Typography variant="h6" gutterBottom>Multiplier Distribution</Typography>
                        <Divider sx={{mb: 2}}/>
                        {multEntries.map(([key, count]) => (
                            <DistributionBar key={key} label={`x${key}`} count={count} maxCount={multMax}/>
                        ))}
                        {multEntries.length === 0 && (
                            <Typography variant="body2" color="text.secondary">No data.</Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Leaderboard */}
            <Grid container spacing={3} sx={{mb: 3}}>
                <Grid size={{xs: 12, md: 6}}>
                    <LeaderboardTable
                        title="Top by Wins"
                        data={leaderboard}
                        valueColumn="totalWins"
                        valueLabel="Wins"
                    />
                </Grid>
            </Grid>

            {/* Session History */}
            <Box sx={{mb: 3}}>
                <Typography variant="h5" gutterBottom>Session History</Typography>
                <SessionsTable minigameId={minigameId}/>
            </Box>

            <Button component={RouterLink} to={`/minigames/stats/${minigameId}`} variant="outlined"
                    startIcon={<ArrowBackIcon/>}>
                Back to Statistics
            </Button>
        </Box>
    );
};

HiLoStatsPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
};

export default HiLoStatsPage;
